import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

const navLinks = [
  { href: "/", label: "Home" },
  { href: "/about", label: "About" },
  { href: "/buy", label: "Buyers" },
  { href: "/sell", label: "Sellers" },
  { href: "/testimonials", label: "Reviews" },
  { href: "/resources", label: "Resources" },
  { href: "/contact", label: "Contact" },
];

export default function Navigation() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl bg-black/90 border-b border-[#D4AF37]/20"
      data-testid="navigation-header"
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3"
            data-testid="logo-link"
          >
            <div className="flex flex-col items-center leading-none">
              <span className="text-[10px] md:text-xs tracking-[0.3em] text-[#D4AF37]">• SREG •</span>
              <span className="text-[9px] md:text-[11px] tracking-[0.12em] text-zinc-300 uppercase">The Shalabi Real Estate Group</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium uppercase tracking-wider transition-colors duration-300 ${
                  isActive(link.href)
                    ? "text-[#D4AF37]"
                    : "text-zinc-400 hover:text-white"
                }`}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:+19092335411"
              className="flex items-center gap-2 text-[#D4AF37] font-semibold"
              data-testid="phone-link-desktop"
            >
              <Phone className="w-4 h-4" />
              <span>909-233-5411</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div
            className="lg:hidden absolute top-20 left-0 right-0 bg-black/95 backdrop-blur-xl border-b border-white/10 animate-fadeIn"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col py-6 px-6">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`py-3 text-lg font-medium uppercase tracking-wider transition-colors ${
                    isActive(link.href)
                      ? "text-[#D4AF37]"
                      : "text-zinc-400 hover:text-white"
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
              <a
                href="tel:+19092335411"
                className="mt-6 py-3"
                data-testid="phone-link-mobile"
              >
                <Button className="w-full bg-[#D4AF37] text-black hover:bg-[#F3E5AB] rounded-none font-bold uppercase tracking-widest">
                  <Phone className="w-4 h-4 mr-2" />
                  Call 909-233-5411
                </Button>
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
